/**
 * Az `FxApi` egyetlen implementációja: a játéklogika ezen keresztül kér
 * hangot, részecskét, rázást, villanást és üzenetet.
 *
 * Miért egy közös felület: így a harci kód sosem hivatkozik közvetlenül
 * a rendererre vagy a hangmotorra, és a visszajelzések (ami a „jó lövés
 * érzet" 80%-a) egyetlen helyen hangolhatók.
 *
 * Gazda: C
 */

import * as THREE from 'three'
import type { FxApi, ImpactKind } from '../core/contracts'
import type { Renderer } from './renderer'
import type { AudioEngine } from '../assets/audio'
import type { Hud } from '../ui/hud'

export class Fx implements FxApi {
  constructor(
    private readonly renderer: Renderer,
    private readonly audio: AudioEngine,
    private readonly hud: Hud,
  ) {}

  sound(id: string, pos: THREE.Vector3 | null = null, volume = 1, pitch = 1): void {
    this.audio.play(id, pos, volume, pitch)
  }

  muzzleFlash(pos: THREE.Vector3, dir: THREE.Vector3, color: number, radius: number): void {
    this.renderer.effects.muzzleFlash(pos, dir, color, radius)
    // A kézifegyvert külön jelenetben rendereljük, ezért annak a megvilágítását
    // is külön kell megkérni — enélkül a fegyver nem villanna fel.
    this.renderer.muzzleLight(2.4)
  }

  impact(point: THREE.Vector3, normal: THREE.Vector3, kind: ImpactKind): void {
    this.renderer.effects.impact(point, normal, kind)
  }

  blood(point: THREE.Vector3, dir: THREE.Vector3, amount: number): void {
    this.renderer.effects.blood(point, dir, amount)
  }

  explosion(point: THREE.Vector3, radius: number, color = 0xffa030): void {
    this.renderer.effects.explosion(point, radius, color)
    this.audio.play('explosion', point, 1)
    // A rázás a távolsággal csökken — a túloldali robbanás ne rázza a képet.
    this.renderer.shake(0.7)
  }

  tracer(from: THREE.Vector3, to: THREE.Vector3, color: number, width = 0.016): void {
    this.renderer.effects.tracer(from, to, color, width)
  }

  shake(amount: number): void {
    this.renderer.shake(amount)
  }

  screenFlash(color: number, strength: number): void {
    this.renderer.postfx.flash(color, strength)
  }

  hitMarker(lethal: boolean): void {
    this.hud.hitMarker(lethal)
    this.audio.play(lethal ? 'hitMarkerKill' : 'hitMarker', null, 0.5)
  }

  message(text: string): void {
    this.hud.message(text)
  }

  /** Halálkor szétrepülő törmelék — nem szerződéses, C hívja belülről. */
  gib(point: THREE.Vector3, color: number, count: number): void {
    this.renderer.effects.gib(point, color, count)
  }
}
